// API Configuration and Service
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/store/api';
const PRIMARY_TOKEN_KEY = 'auth_token';
const LEGACY_TOKEN_KEY = 'authToken';
const TOKEN_COOKIE_KEY = 'ds_auth_token';
const RECOVERY_TOKEN_KEY = 'auth_token_recovery';
const LOCAL_STORAGE_KEYS_TO_CLEAR = [
  'cart',
  'cartItems',
  'cartItemCount',
  'orders',
  'favorites',
  'subscription',
  'biometric_token',
];
const SESSION_STORAGE_KEYS_TO_CLEAR = [
  'passwordReset',
];

class ApiService {
  constructor() {
    this.baseURL = API_URL;
  }

  getCookieDomain() {
    if (typeof window === 'undefined') {
      return '';
    }

    const hostname = window.location.hostname;
    if (!hostname || hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
      return '';
    }

    // Avoid invalid domain attribute when running on an IP host.
    if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) {
      return '';
    }

    const parts = hostname.split('.').filter(Boolean);
    if (parts.length < 2) {
      return '';
    }

    return `.${parts.slice(-2).join('.')}`;
  }

  getTokenFromCookie() {
    if (typeof document === 'undefined') {
      return null;
    }

    const cookieEntry = document.cookie
      .split(';')
      .map(part => part.trim())
      .find(part => part.startsWith(`${TOKEN_COOKIE_KEY}=`));

    if (!cookieEntry) {
      return null;
    }

    return decodeURIComponent(cookieEntry.substring(TOKEN_COOKIE_KEY.length + 1));
  }

  setTokenCookie(token) {
    if (!token || typeof document === 'undefined') {
      return;
    }

    const domain = this.getCookieDomain();
    const domainAttr = domain ? `; domain=${domain}` : '';
    const secureAttr = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
    const maxAge = 60 * 60 * 24 * 30;

    document.cookie = `${TOKEN_COOKIE_KEY}=${encodeURIComponent(token)}; path=/; max-age=${maxAge}; SameSite=Lax${domainAttr}${secureAttr}`;
  }

  clearTokenCookie() {
    if (typeof document === 'undefined') {
      return;
    }

    const domain = this.getCookieDomain();
    const domainAttr = domain ? `; domain=${domain}` : '';
    const secureAttr = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';

    // Clear both domain and host-only cookie forms.
    document.cookie = `${TOKEN_COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax${domainAttr}${secureAttr}`;
    document.cookie = `${TOKEN_COOKIE_KEY}=; path=/; max-age=0; SameSite=Lax${secureAttr}`;
  }

  // Get auth token from localStorage
  getToken() {
    const primaryToken = localStorage.getItem(PRIMARY_TOKEN_KEY);
    if (primaryToken) {
      if (!localStorage.getItem(LEGACY_TOKEN_KEY)) {
        localStorage.setItem(LEGACY_TOKEN_KEY, primaryToken);
      }
      localStorage.setItem(RECOVERY_TOKEN_KEY, primaryToken);
      this.setTokenCookie(primaryToken);
      return primaryToken;
    }

    const legacyToken = localStorage.getItem(LEGACY_TOKEN_KEY);
    if (legacyToken) {
      localStorage.setItem(PRIMARY_TOKEN_KEY, legacyToken);
      localStorage.setItem(RECOVERY_TOKEN_KEY, legacyToken);
      this.setTokenCookie(legacyToken);
      return legacyToken;
    }

    const cookieToken = this.getTokenFromCookie();
    if (cookieToken) {
      localStorage.setItem(PRIMARY_TOKEN_KEY, cookieToken);
      localStorage.setItem(LEGACY_TOKEN_KEY, cookieToken);
      localStorage.setItem(RECOVERY_TOKEN_KEY, cookieToken);
      return cookieToken;
    }

    const recoveryToken = localStorage.getItem(RECOVERY_TOKEN_KEY);
    if (recoveryToken) {
      localStorage.setItem(PRIMARY_TOKEN_KEY, recoveryToken);
      localStorage.setItem(LEGACY_TOKEN_KEY, recoveryToken);
      this.setTokenCookie(recoveryToken);
      return recoveryToken;
    }

    return null;
  }

  // Set auth token in localStorage
  setToken(token) {
    if (!token) {
      return;
    }

    localStorage.setItem(PRIMARY_TOKEN_KEY, token);
    localStorage.setItem(LEGACY_TOKEN_KEY, token);
    localStorage.setItem(RECOVERY_TOKEN_KEY, token);
    this.setTokenCookie(token);
  }

  // Remove auth token from localStorage
  removeToken(options = {}) {
    const {
      preserveRecovery = false,
      clearUser = true,
      clearAppState = false,
      clearAllStorage = false,
    } = options;

    if (clearAllStorage) {
      if (typeof localStorage !== 'undefined') {
        localStorage.clear();
      }

      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.clear();
      }

      this.clearTokenCookie();
      return;
    }

    localStorage.removeItem(PRIMARY_TOKEN_KEY);
    localStorage.removeItem(LEGACY_TOKEN_KEY);

    if (!preserveRecovery) {
      localStorage.removeItem(RECOVERY_TOKEN_KEY);
    }

    if (clearUser) {
      localStorage.removeItem('user');
    }

    if (clearAppState) {
      this.clearClientSessionData();
    }

    this.clearTokenCookie();
  }

  clearClientSessionData() {
    if (typeof localStorage !== 'undefined') {
      LOCAL_STORAGE_KEYS_TO_CLEAR.forEach((key) => {
        localStorage.removeItem(key);
      });
    }

    if (typeof sessionStorage !== 'undefined') {
      SESSION_STORAGE_KEYS_TO_CLEAR.forEach((key) => {
        sessionStorage.removeItem(key);
      });
    }
  }

  // Get user data from localStorage
  getUser() {
    const user = localStorage.getItem('user');
    if (!user || user === 'undefined' || user === 'null') {
      return null;
    }

    try {
      return JSON.parse(user);
    } catch (error) {
      console.warn('Invalid cached user payload detected, clearing malformed value.');
      localStorage.removeItem('user');
      return null;
    }
  }

  // Set user data in localStorage
  setUser(user) {
    if (!user || typeof user !== 'object') {
      localStorage.removeItem('user');
      return;
    }

    localStorage.setItem('user', JSON.stringify(user));
  }

  // Check if user is authenticated
  isAuthenticated() {
    return !!this.getToken();
  }

  // Build headers with auth token
  getHeaders(includeAuth = false, contentType = 'application/json') {
    const headers = {
      'Accept': 'application/json',
    };

    if (contentType) {
      headers['Content-Type'] = contentType;
    }

    if (includeAuth) {
      const token = this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    return headers;
  }

  // Generic request method
  async request(endpoint, options = {}) {
    const {
      auth = false,
      logoutOnUnauthorized = true,
      clearTokenOnUnauthorized = false,
      headers: customHeaders = {},
      ...fetchOptions
    } = options;

    const url = `${this.baseURL}${endpoint}`;
    const config = {
      ...fetchOptions,
      headers: {
        ...this.getHeaders(auth),
        ...customHeaders,
      },
    };

    try {
      console.log('API Request:', url, config && config.method ? config.method : 'GET');
      const response = await fetch(url, config);
      let data;
      try {
        data = await response.json();
      } catch (e) {
        data = null;
      }

      // Avoid destroying local auth state on transient 401s during SPA bootstrapping.
      if (response.status === 401 && auth && logoutOnUnauthorized) {
        if (clearTokenOnUnauthorized) {
          this.removeToken();
          window.dispatchEvent(new CustomEvent('auth:logout'));
        } else {
          window.dispatchEvent(new CustomEvent('auth:unauthorized', {
            detail: {
              endpoint,
              status: response.status,
            },
          }));
        }
      }

      return {
        success: response.ok,
        status: response.status,
        data,
      };
    } catch (error) {
      console.error('API Request Error:', error);
      return {
        success: false,
        status: 0,
        data: {
          message: 'Network error. Please check your connection.',
        },
      };
    }
  }

  // POST request
  async post(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // GET request
  async get(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'GET',
      ...options,
    });
  }

  // PUT request
  async put(endpoint, data, options = {}) {
    return this.request(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
      ...options,
    });
  }

  // DELETE request
  async delete(endpoint, options = {}) {
    return this.request(endpoint, {
      method: 'DELETE',
      ...options,
    });
  }

  // Auth API Methods
  async register(userData) {
    const response = await this.post('/register', userData);
    
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    
    return response;
  }

  async login(credentials) {
    const response = await this.post('/login', credentials);
    
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    
    return response;
  }

   async loginWithMobile(credentials) {
    // For mobile login, send OTP first instead of direct login
    const response = await this.post('/login/otp/mobile/send', { 
      mobile: credentials.mobile 
    });
    
    // Note: Token will be set after OTP verification, not here
    return response;
  }

  async logout() {
    const response = await this.post('/logout', {}, { auth: true, logoutOnUnauthorized: false });
    this.removeToken({ clearAppState: true, clearAllStorage: true });
    return response;
  }

  async forgotPassword(email) {
    return this.post('/forgot-password', { email });
  }

  async verifyOtp(email, otp) {
    return this.post('/verify-otp', { email, otp });
  }

   async verifyMobileOtp(mobile, otp) {
    const response = await this.post('/login/otp/mobile/verify', { mobile, otp });
    
    // Save token and user after successful OTP verification
    if (response.success && response.data.token) {
      this.setToken(response.data.token);
      this.setUser(response.data.user);
    }
    
    return response;
  }

  async resetPassword(email,otp, password, password_confirmation) {
    return this.post('/reset-password', {
      email,
      otp,
      password,
      password_confirmation,
    });
  }

  async getAuthenticatedUser(options = {}) {
    const primaryResponse = await this.get('/user', { auth: true, ...options });

    if (primaryResponse.success) {
      return primaryResponse;
    }

    // Some deployments expose authenticated profile endpoints under different paths.
    if (primaryResponse.status === 401 || primaryResponse.status === 403) {
      const fallbackOptions = { auth: true, logoutOnUnauthorized: false, ...options };

      const profileResponse = await this.get('/profile', fallbackOptions);
      if (profileResponse.success) {
        const resolvedUser = profileResponse?.data?.user || profileResponse?.data;
        if (resolvedUser) {
          return {
            ...profileResponse,
            data: {
              ...(profileResponse.data || {}),
              user: resolvedUser,
            },
          };
        }
      }

      const legacyProfileResponse = await this.get('/user/profile', fallbackOptions);
      if (legacyProfileResponse.success) {
        const resolvedUser =
          legacyProfileResponse?.data?.user ||
          legacyProfileResponse?.data?.data?.user ||
          legacyProfileResponse?.data;

        if (resolvedUser) {
          return {
            ...legacyProfileResponse,
            data: {
              ...(legacyProfileResponse.data || {}),
              user: resolvedUser,
            },
          };
        }
      }
    }

    return primaryResponse;
  }

  // Social Auth Methods
  redirectToGoogle() {
    window.location.href = `${this.baseURL}/auth/google`;
  }

  redirectToGithub() {
    window.location.href = `${this.baseURL}/auth/github`;
  }

  // Handle social auth callback
  handleSocialCallback() {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    
    if (token) {
      this.setToken(token);
      // Fetch user data
      this.getAuthenticatedUser().then(response => {
        if (response.success) {
          this.setUser(response.data.user);
          window.location.href = '/';
        }
      });
    }
  }

  // Additional API methods can be added here
  async sendOTPForLogin(mobile){
    return this.post('/login/otp/mobile/send', { mobile});
  }

  async connectWhatsApp(whatsappNumber) {
    return this.put('/user/connect-whatsapp', { 
      whatsapp_number: whatsappNumber, 
      whatsapp_enabled: true 
    }, { auth: true },{ token: this.getToken() });
  }

  async updateWhatsAppPreference(enabled) {
    return this.put('/user/whatsapp-preference', { 
      whatsapp_enabled: enabled 
    }, { auth: true },{ token: this.getToken() });
  }

  async updateUserProfile(profileData) {
    console.log("API Profile Data:"+JSON.stringify(profileData));
    return this.put('/user/update-profile', profileData, { auth: true });
  }

  async fetchDeliveryAddresses() {
    return this.get('/user/delivery-addresses', { auth: true });
  }

  async addDeliveryAddress(addressData) {
    return this.post('/user/delivery-addresses', addressData, { auth: true });
  }

  async updateDeliveryAddress(addressId, addressData) {
    return this.put(`/user/delivery-addresses/${addressId}`, addressData, { auth: true });
  }

  async deleteDeliveryAddress(addressId) {
    return this.delete(`/user/delivery-addresses/${addressId}`, { auth: true });
  }

  async setDefaultDeliveryAddress(addressId) {
    return this.put(`/user/delivery-addresses/${addressId}/set-default`, {}, { auth: true });
  }

    // ============================================
  // CART API METHODS
  // ============================================
  
  // Get user cart from backend
  async getCart() {
    return this.get('/cart', { auth: true });
  }

  // Add item to cart
  async addToCart(productData) {
    return this.post('/cart', {
      product_id: productData.id,
      quantity: productData.quantity || 1,
      price: productData.price
    }, { auth: true });
  }

  // Update cart item quantity
  async updateCartItem(cartItemId, quantity) {
    return this.put(`/cart/${cartItemId}`, {
      quantity
    }, { auth: true });
  }

  // Remove item from cart
  async removeFromCart(cartItemId) {
    return this.delete(`/cart/${cartItemId}`, { auth: true });
  }

  // Clear entire cart
  async clearCart() {
    return this.delete('/cart', { auth: true });
  }

  // Get cart total
  async getCartTotal() {
    return this.get('/cart/total', { auth: true });
  }

  // Sync local cart with backend (for migration from localStorage)
  async syncCart(cartItems) {
    return this.post('/cart/sync', {
      items: cartItems.map(item => ({
        product_id: item.id,
        quantity: item.quantity,
        price: item.price
      }))
    }, { auth: true });
  }

  // ============================================
  // ORDERS API METHODS
  // ============================================
  
  // Create order from cart
  async createOrder(orderData) {
    console.log("API Order Data:"+JSON.stringify(orderData));
   
    // Normalize delivery address id from various possible callers
    const deliveryAddressId = orderData?.delivery_address_id || orderData?.deliveryAddressId || orderData?.deliveryAddressesId || (orderData?.deliveryAddress && (orderData.deliveryAddress.id || orderData.deliveryAddress.address_id)) || null;

    // Build payload including common order fields (items, amounts, coupon)
    const payload = {
      delivery_address_id: deliveryAddressId,
      delivery_slot: orderData?.deliverySlot || orderData?.delivery_slot || '',
      payment_method: orderData?.paymentMethod || orderData?.payment_method || '',
      notes: orderData?.notes || '',
      items: orderData?.items || orderData?.order_items || [],
      total_amount: orderData?.totalAmount || orderData?.total_amount || null,
      original_amount: orderData?.originalAmount || orderData?.original_amount || null,
      coupon_code: orderData?.couponCode || orderData?.coupon_code || null,
      coupon_discount: orderData?.couponDiscount || orderData?.coupon_discount || 0
    };

    console.log('Posting order payload:', payload);

    return this.post('/orders', payload, { auth: true });
  }

  // Get user orders
  async getMyOrders(page = 1, perPage = 10, filters = {}) {
    const params = new URLSearchParams({
      page: page.toString(),
      per_page: perPage.toString(),
      ...filters
    });
    return this.get(`/orders?${params.toString()}`, { auth: true });
  }

  // Get single order details
  async getOrderById(orderId) {
    return this.get(`/orders/${orderId}`, { auth: true });
  }

  // Cancel order
  async cancelOrder(orderId, reason) {
    return this.put(`/orders/${orderId}/cancel`, {
      reason: reason || 'Customer requested cancellation'
    }, { auth: true });
  }

  // Track order
  async trackOrder(orderNumber) {
    console.log('🚚 API: Tracking order with identifier:', orderNumber);
    console.log('🔗 Endpoint:', `/orders/track/${orderNumber}`);
    
    try {
      // Try primary endpoint first
      const response = await this.get(`/orders/track/${orderNumber}`, { auth: true });
      console.log('📥 Primary endpoint response:', response);
      return response;
    } catch (error) {
      console.log('⚠️ Primary endpoint failed, trying alternative...');
      console.error('Primary error:', error);
      
      // Try alternative endpoint format
      try {
        const altResponse = await this.get(`/orders/${orderNumber}/track`, { auth: true });
        console.log('📥 Alternative endpoint response:', altResponse);
        return altResponse;
      } catch (altError) {
        console.error('❌ Both endpoints failed:', altError);
        throw error; // Throw original error
      }
    }
  }

  // Rate order/product
  async rateOrder(orderId, ratingData) {
    return this.post(`/orders/${orderId}/rate`, {
      rating: ratingData.rating,
      review: ratingData.review || '',
      product_id: ratingData.productId
    }, { auth: true });
  }

  // ============================================
  // PRODUCTS API METHODS
  // ============================================
  
  // Get all products
  async getProducts(filters = {}) {
    const queryParams = new URLSearchParams(filters).toString();
    const endpoint = queryParams ? `/products?${queryParams}` : '/products';
    return this.get(endpoint);
  }

  // Get single product
  async getProductById(productId) {
    return this.get(`/products/${productId}`);
  }

  // Search products
  async searchProducts(query) {
    return this.get(`/products/search?q=${encodeURIComponent(query)}`);
  }

  // Get product by category
  async getProductsByCategory(category) {
    return this.get(`/products/category/${category}`);
  }

  // Get feature sections with their products
  async getFeatureSections() {
    return this.get('/feature-sections');
  }

  // ============================================
  // SUBSCRIPTION API METHODS
  // ============================================
  
  // Create subscription
  async createSubscription(subscriptionData) {
    return this.post('/subscriptions', {
      product_id: subscriptionData.productId,
      frequency: subscriptionData.planId,
      delivery_slot: subscriptionData.deliverySlot,
      delivery_address_id: subscriptionData.addressId,
      start_date: subscriptionData.startDate,
      payment_method: subscriptionData.paymentMethod,
      status: subscriptionData.status || 'active',
      quantity: subscriptionData.quantity || 1
    }, { auth: true });
  }

  // Get user subscriptions
  async getMySubscriptions() {
    return this.get('/subscriptions', { auth: true });
  }

  // Pause subscription
  async pauseSubscription(subscriptionId, pauseUntil) {
    return this.post(`/subscriptions/${subscriptionId}/pause`, {
      pause_until: pauseUntil
    }, { auth: true });
  }

  // Resume subscription
  async resumeSubscription(subscriptionId) {
    return this.post(`/subscriptions/${subscriptionId}/resume`, {}, { auth: true });
  }

  // Cancel subscription
  async cancelSubscription(subscriptionId, reason) {
    return this.post(`/subscriptions/${subscriptionId}/cancel`, {
      reason: reason || 'Customer requested cancellation'
    }, { auth: true });
  }

  // Reactivate a cancelled subscription
  async reactivateSubscription(subscriptionId) {
    return this.post(`/subscriptions/${subscriptionId}/reactivate`, {}, { auth: true });
  }

  // Update subscription plan
  async updateSubscriptionPlan(subscriptionId, planData) {    
    return this.put(`/subscriptions/${subscriptionId}`, {
      frequency: planData.planId,
      delivery_slot: planData.deliverySlot,
      delivery_address_id: planData.addressId,
      quantity: planData.quantity || 1
    }, { auth: true });
  }

  // ============================================
  // FAVORITES/WISHLIST API METHODS
  // ============================================
  
  // Get favorites
  async getFavorites(page = 1, perPage = 100) {
    const params = new URLSearchParams();
    if (page) {
      params.set('page', String(page));
    }
    if (perPage) {
      params.set('per_page', String(perPage));
    }

    const query = params.toString();
    return this.get(`/favorites${query ? `?${query}` : ''}`, { auth: true });
  }

  // Add to favorites
  async addToFavorites(productId) {
    return this.post('/favorites', {
      product_id: productId
    }, { auth: true });
  }

  // Remove from favorites
  async removeFromFavorites(favoriteId) {
    return this.delete(`/favorites/${favoriteId}`, { auth: true });
  }

  // ============================================
  // NOTIFICATIONS API METHODS
  // ============================================
  
  // Get notifications
  async getNotifications() {
    return this.get('/notifications', { auth: true });
  }

  // Mark notification as read
  async markNotificationRead(notificationId) {
    return this.put(`/notifications/${notificationId}/read`, {}, { auth: true });
  }

  // Mark all notifications as read
  async markAllNotificationsRead() {
    return this.put('/notifications/mark-all-read', {}, { auth: true });
  }

  // Delete notification
  async deleteNotification(notificationId) {
    return this.delete(`/notifications/${notificationId}`, { auth: true });
  }

  // Clear all notifications
  async clearNotifications() {
    return this.delete('/notifications', { auth: true });
  }

  // ============================================
  // PAYMENT METHODS API
  // ============================================
  
  // Get saved payment methods
  async getPaymentMethods() {
    return this.get('/payment-methods', { auth: true });
  }

  // Add payment method
  async addPaymentMethod(paymentData) {
    return this.post('/payment-methods/add', {
      type: paymentData.type, // 'card', 'upi', 'wallet'
      details: paymentData.details,
      is_default: paymentData.isDefault || false
    }, { auth: true });
  }

  // Update payment method
  async updatePaymentMethod(paymentMethodId, paymentData) {
    return this.put(`/payment-methods/${paymentMethodId}`, paymentData, { auth: true });
  }

  // Delete payment method
  async deletePaymentMethod(paymentMethodId) {
    return this.delete(`/payment-methods/${paymentMethodId}`, { auth: true });
  }

  // Set default payment method
  async setDefaultPaymentMethod(paymentMethodId) {
    return this.put(`/payment-methods/${paymentMethodId}/set-default`, {}, { auth: true });
  }

  // ============================================
  // PAYU PAYMENT METHODS
  // ============================================

  // Initiate a PayU payment for a given order - returns form params + PayU URL
  async initiatePayuPayment(orderId, phone = null) {
    const normalizedOrderId = Number(orderId);
    const resolvedOrderId = Number.isFinite(normalizedOrderId) && normalizedOrderId > 0
      ? normalizedOrderId
      : orderId;

    if (!resolvedOrderId || resolvedOrderId === 'undefined' || resolvedOrderId === 'null') {
      return {
        success: false,
        status: 422,
        data: {
          message: 'Validation failed',
          errors: {
            order_id: ['The order id field is required.']
          }
        }
      };
    }

    return this.post(
      '/payments/payu/initiate',
      { order_id: resolvedOrderId, orderId: resolvedOrderId, phone },
      { auth: true }
    );
  }

  // Poll the server to check if a PayU transaction has been verified
  async verifyPayuTransaction(txnid) {
    return this.get(`/payments/payu/verify/${txnid}`, { auth: true });
  }
  
  // Get FAQ
  async getFAQ() {
    return this.get('/support/faq');
  }

  // Submit support ticket
  async submitSupportTicket(ticketData) {
    return this.post('/support/ticket', {
      subject: ticketData.subject,
      category: ticketData.category,
      description: ticketData.description,
      priority: ticketData.priority || 'medium'
    }, { auth: true });
  }

  // Get user tickets
  async getMyTickets() {
    return this.get('/support/my-tickets', { auth: true });
  }

  // Get ticket by ID
  async getTicketById(ticketId) {
    return this.get(`/support/ticket/${ticketId}`, { auth: true });
  }

  // Reply to ticket
  async replyToTicket(ticketId, message) {
    return this.post(`/support/ticket/${ticketId}/reply`, {
      message
    }, { auth: true });
  }

  // ─── Wallet ───────────────────────────────────────────────────────────────

  async getWallet() {
    return this.get('/wallet', { auth: true });
  }

  async getWalletTransactions(page = 1) {
    return this.get(`/wallet/transactions?page=${page}`, { auth: true });
  }

  async initiateWalletTopup(amount, payment_gateway = 'razorpay') {
    return this.post('/wallet/topup', { amount, payment_gateway }, { auth: true });
  }

  async verifyWalletTopup(data) {
    return this.post('/wallet/topup/verify', data, { auth: true });
  }

}


// Create singleton instance
const apiService = new ApiService();

// Export for use in components
export default apiService;
